import { IIdentifierWrite, IInstanceIdentifierSchema, IInstanceIdentifierValues } from 'src/api/recordIdentifiers';
import { IIntegrationGroup } from './types';

/**
 * React Hook Form reads a dot or a bracket in a field name as a path into nested values, so
 * the instance and the key are joined with something it leaves alone. Instance ids are
 * uuids and keys are snake_case, so neither can contain this.
 */
const SEPARATOR = '--';

/** The form field name for one key on one instance. */
export const fieldName = (instanceId: string, key: string): string => `${instanceId}${SEPARATOR}${key}`;

/**
 * Turns the declarations into the form's groups, one per declared key.
 *
 * Grouped by key rather than by instance because the declaration is what repeats: three
 * instances of one addon share a label and help text, and only the value differs. Showing
 * that once and listing the instances under it is the whole point of this shape.
 *
 * Keyed on the addon as well as the key, so two addons that happen to declare the same key
 * name do not merge into one group under whichever label came first.
 *
 * An instance whose addon declares nothing for this record type never reaches here: core
 * has already filtered to the record type and applied the instance's narrowing.
 *
 * @param schema - What the brand's instances declare for this record type
 * @returns One group per declared key, in the order the declarations arrive
 */
export const buildGroups = (schema: IInstanceIdentifierSchema[] = []): IIntegrationGroup[] => {
  const groups = new Map<string, IIntegrationGroup>();

  schema.forEach(
    ({
      instance_id: instanceId,
      instance_name: instanceName,
      addon_id: addonId,
      addon_name: addonName,
      declarations,
    }) => {
      declarations.forEach((declaration) => {
        const id = `${addonId}::${declaration.key}`;
        const existing = groups.get(id);
        const entry = {
          name: fieldName(instanceId, declaration.key),
          instanceId,
          // The name is localised and can be missing, so it falls back rather than
          // labelling an input "null".
          instanceName: instanceName || 'Unnamed instance',
        };

        if (existing) {
          existing.entries.push(entry);
          return;
        }

        groups.set(id, { id, declaration, addonName, entries: [entry] });
      });
    },
  );

  return [...groups.values()];
};

/**
 * The form's starting values: every declared field, carrying what the record holds.
 *
 * Every field is present and defaults to the empty string, so a field the record has no
 * value for is untouched rather than undefined, and React Hook Form can tell a cleared
 * field from one that was never filled in.
 *
 * @param schema - What the brand's instances declare
 * @param values - What the record currently holds
 * @returns Field name to value, empty string where there is nothing stored
 */
export const storedValues = (
  schema: IInstanceIdentifierSchema[] = [],
  values: IInstanceIdentifierValues[] = [],
): Record<string, string> => {
  const stored = new Map(
    values.flatMap(({ instance_id: instanceId, identifiers }) =>
      identifiers.map(({ key, value }) => [fieldName(instanceId, key), value] as const),
    ),
  );

  return Object.fromEntries(
    buildGroups(schema).flatMap(({ entries }) => entries.map(({ name }) => [name, stored.get(name) ?? ''])),
  );
};

/**
 * The writes a submit should send: the fields the operator actually changed.
 *
 * Only dirty fields are sent, so saving one key never rewrites another instance's value
 * and never resurrects a key somebody else cleared in the meantime. A field cleared to the
 * empty string is sent as null, which deletes it rather than storing an empty value.
 *
 * @param groups - The form's groups
 * @param formValues - The submitted values
 * @param dirtyFields - React Hook Form's dirty map
 * @returns The payload for the write, empty when nothing changed
 */
export const writesFrom = (
  groups: IIntegrationGroup[],
  formValues: Record<string, string>,
  dirtyFields: Partial<Record<string, boolean>>,
): IIdentifierWrite[] =>
  groups.flatMap(({ declaration, entries }) =>
    entries
      .filter(({ name }) => dirtyFields[name])
      .map(({ name, instanceId }) => {
        const value = formValues[name];
        return {
          instance_id: instanceId,
          key: declaration.key,
          value: value === '' || value === undefined ? null : value,
        };
      }),
  );
